import { useState, useEffect } from "react";
import { ChevronDown, ChevronRight, CheckCircle2, Loader2, XCircle, Image, FileText, FileStack } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

export type AttachmentRowStatus = "pending" | "analyzing" | "completed" | "error";

export interface AttachmentFileRow {
  id: string;
  fileName: string;
  status: AttachmentRowStatus;
  tagCount?: number;
  errorMessage?: string;
}

export interface AttachmentKindBlock {
  kind: "photo" | "plan" | "doc";
  files: AttachmentFileRow[];
}

interface AttachmentAnalysisRowsProps {
  blocks: AttachmentKindBlock[];
}

const KIND_META: Record<AttachmentKindBlock["kind"], { label: string; icon: typeof Image }> = {
  photo: { label: "Photos", icon: Image },
  plan: { label: "Drawings", icon: FileStack },
  doc: { label: "Documents", icon: FileText },
};

function FileRow({ file }: { file: AttachmentFileRow }) {
  return (
    <div className="flex items-center gap-2 min-w-0 text-xs">
      <div className="flex-shrink-0">
        {file.status === "analyzing" && <Loader2 className="h-3.5 w-3.5 text-primary animate-spin" />}
        {file.status === "completed" && <CheckCircle2 className="h-3.5 w-3.5 text-primary" />}
        {file.status === "error" && <XCircle className="h-3.5 w-3.5 text-destructive" />}
        {file.status === "pending" && <div className="h-3.5 w-3.5 rounded-full border border-muted-foreground/30" />}
      </div>
      <span className={cn(
        "truncate flex-1 min-w-0",
        file.status === "analyzing" && "text-foreground",
        file.status === "completed" && "text-muted-foreground",
        file.status === "pending" && "text-muted-foreground/60",
        file.status === "error" && "text-destructive",
      )}>
        {file.fileName}
      </span>
      {file.status === "completed" && typeof file.tagCount === "number" && file.tagCount > 0 && (
        <span className="flex-shrink-0 text-muted-foreground tabular-nums">
          {file.tagCount} {file.tagCount === 1 ? "tag" : "tags"}
        </span>
      )}
    </div>
  );
}

function KindBlock({ block }: { block: AttachmentKindBlock }) {
  const meta = KIND_META[block.kind];
  const Icon = meta.icon;
  const total = block.files.length;
  const completed = block.files.filter(f => f.status === "completed" || f.status === "error").length;
  const allDone = total > 0 && completed === total;

  // Auto-collapse on completion
  const [open, setOpen] = useState(true);
  useEffect(() => {
    if (allDone) {
      const t = setTimeout(() => setOpen(false), 600);
      return () => clearTimeout(t);
    }
  }, [allDone]);

  if (total === 0) return null;

  return (
    <div className="min-w-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        {open ? <ChevronDown className="h-3 w-3 flex-shrink-0" /> : <ChevronRight className="h-3 w-3 flex-shrink-0" />}
        <Icon className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="truncate">{meta.label}</span>
        <span className="ml-auto flex-shrink-0 tabular-nums">{completed}/{total}</span>
      </button>
      {open && (
        <div className="mt-1.5 ml-5 space-y-1 min-w-0">
          {block.files.map(f => <FileRow key={f.id} file={f} />)}
        </div>
      )}
    </div>
  );
}

export function AttachmentAnalysisRows({ blocks }: AttachmentAnalysisRowsProps) {
  const visibleBlocks = blocks.filter(b => b.files.length > 0);
  if (visibleBlocks.length === 0) return null;

  return (
    <div className="mb-6 min-w-0">
      <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        Attachment Analysis
      </h4>
      <div className="space-y-3">
        {visibleBlocks.map(b => <KindBlock key={b.kind} block={b} />)}
      </div>
    </div>
  );
}
